import React, { useEffect, useState, useRef } from "react";
import Car from "../components/GasMeUp/Car";
import Terrain from "../components/GasMeUp/Terrain";
import EconomyBar from "../components/GasMeUp/EconomyBar";
import Controls from "../components/GasMeUp/Controls";
import EventBubble from "../components/GasMeUp/EventBubble";

// --- Helper functions for terrain ---
const smooth = (arr, n) => arr.map((_, i) => {
  const start = Math.max(0, i - n);
  const end = Math.min(arr.length, i + n + 1);
  const subset = arr.slice(start, end);
  return subset.reduce((a, b) => a + b, 0) / subset.length;
});

// --- Example stock data and events ---
const rawStockPrices = [
  100, 102, 104, 101, 98, 110, 120, 125, 130, 128, 
  140, 145, 150, 160, 170, 180, 175, 172, 180, 200
];
const stockEvents = {
  3: "3Q Miss",
  7: "Product Launch",
  12: "Scandal",
  16: "Earnings Beat"
};

const initialPrice = rawStockPrices[0];
const goalMoney = initialPrice * 100;  // CHANGED TO 10x

const width = 800;
const height = 400;
const groundY = 300;

const getTerrainPoints = (prices) => {
  const smoothed = smooth(prices, 2);
  const max = Math.max(...smoothed);
  const min = Math.min(...smoothed);
  return smoothed.map((p, i) => ({
    x: 60 + i * 35,
    y: groundY - ((p - min) / (max - min)) * 180
  }));
};

const GasMeUp = () => {
  // --- Game State ---
  const [car, setCar] = useState({
    x: 60,
    y: groundY - 10,
    vx: 0,
    vy: 0,
    fuel: 100,
    rotation: 0,
    onGround: true,
    flipping: false
  });
  const [economy, setEconomy] = useState({
    savings: 100,
    stock: 0,
  });
  const [gameState, setGameState] = useState("playing");
  const [timer, setTimer] = useState(0);
  const [shake, setShake] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(initialPrice);
  
  const points = getTerrainPoints(rawStockPrices);
  const smoothedPrices = useRef(smooth(rawStockPrices, 2)).current;

  // --- Update current price based on car position ---
  useEffect(() => {
    if (gameState !== "playing") return;
    
    const idx = Math.floor((car.x - 60) / 35);
    if (idx >= 0 && idx < smoothedPrices.length) {
      setCurrentPrice(smoothedPrices[idx]);
    }
  }, [car.x, gameState, smoothedPrices]);

  // --- Physics and Game Loop ---
  useEffect(() => {
    if (gameState !== "playing") return;
    
    const interval = setInterval(() => {
      setTimer((t) => t + 1);

      setCar((car) => {
        let { x, y, vx, vy, fuel, rotation, onGround } = car;
        // Get terrain slope
        const idx = Math.floor((x - 60) / 35);
        const nextIdx = Math.min(points.length - 1, idx + 1);
        const slope = (points[nextIdx].y - points[idx].y) / (points[nextIdx].x - points[idx].x);

        // Simple gravity and movement
        if (!onGround) vy += 0.5;
        x += vx;
        y += vy;
        rotation = Math.atan(slope) * (180 / Math.PI);

        // Ground collision
        if (y > points[idx].y - 10) {
          y = points[idx].y - 10;
          vy = 0;
          onGround = true;
        } else {
          onGround = false;
        }

        return { x, y, vx, vy, fuel, rotation, onGround, flipping: car.flipping };
      });
    }, 40);
    return () => clearInterval(interval);
  }, [gameState, points]);

  // --- Win/Lose Condition Check ---
  useEffect(() => {
    if (gameState !== "playing") return;
    
    const total = economy.savings + economy.stock * currentPrice;
    
    if (total >= goalMoney) {
      setGameState("win");
    } else if (total <= 0 || car.fuel <= 0) {
      setGameState("lose");
    }
  }, [economy, car.fuel, currentPrice, gameState]);

  // --- Controls ---
  const drive = () => {
    if (car.fuel <= 0 || gameState !== "playing") return;
    setCar((car) => ({ ...car, vx: 3, fuel: Math.max(0, car.fuel - 0.2) }));
  };
  
  const driveFast = () => {
    if (car.fuel <= 0 || gameState !== "playing") return;
    setCar((car) => ({ ...car, vx: 5, fuel: Math.max(0, car.fuel - 0.4) }));
  };
  
  const brake = () => {
    setCar((car) => ({ ...car, vx: Math.max(0, car.vx - 1) }));
  };
  
  const buyFuel = () => {
    if (economy.savings < currentPrice) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    setEconomy((e) => ({
      ...e,
      savings: e.savings - currentPrice
    }));
    setCar((car) => ({ ...car, fuel: car.fuel + 30 }));
  };
  
  const buyStock = () => {
    if (economy.savings < currentPrice) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    setEconomy((e) => ({
      ...e,
      savings: e.savings - currentPrice,
      stock: e.stock + 1
    }));
  };
  
  const sellStock = () => {
    if (economy.stock < 1) {
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }
    setEconomy((e) => ({
      ...e,
      savings: e.savings + currentPrice,
      stock: e.stock - 1
    }));
  };

  // --- Render ---
  const total = economy.savings + economy.stock * currentPrice;

  return (
    <div>
      <h2>GasMeUp</h2>
      <div style={{ marginBottom: 10 }}>
        <span style={{ fontWeight: "bold" }}>Goal: ${goalMoney.toFixed(2)}</span>
      </div>
      <EconomyBar
        savings={economy.savings}
        stockValue={economy.stock * currentPrice}
        total={total}
      />
      <div style={{
        color: shake ? "red" : "#222",
        transition: "color 0.2s",
        fontWeight: "bold",
        fontSize: 22,
        margin: 6,
        display: "inline-block",
        transform: shake ? "translateX(4px)" : "none"
      }}>
        ${total.toFixed(2)}
      </div>
      <svg width={width} height={height} style={{ background: "#eef" }}>
        {/* Horizontal price lines */}
        {[50, 100, 150, 200].map((p) => (
          <g key={p}>
            <line 
              x1={0} 
              x2={width} 
              y1={groundY - ((p - 98) / (170 - 98)) * 180} 
              y2={groundY - ((p - 98) / (170 - 98)) * 180}
              stroke="#bbb" 
              strokeDasharray="3 5" 
            />
            <text 
              x={5} 
              y={groundY - ((p - 98) / (170 - 98)) * 180 - 4} 
              fontSize={10} 
              fill="#aaa"
            >
              ${p}
            </text>
          </g>
        ))}
        {/* Terrain */}
        <Terrain points={points} />
        {/* Event Bubbles */}
        {Object.entries(stockEvents).map(([idx, text]) => (
          <EventBubble
            key={idx}
            x={points[idx]?.x || 0}
            y={points[idx]?.y || 0}
            text={text}
          />
        ))}
        {/* Car */}
        <Car x={car.x} y={car.y} rotation={car.rotation} />
      </svg>
      <div style={{ margin: 12 }}>
        <span>Fuel: {car.fuel.toFixed(1)}</span>
      </div>
      <Controls
        onDrive={drive}
        onDriveFast={driveFast}
        onBrake={brake}
        onBuyFuel={buyFuel}
        onBuyStock={buyStock}
        onSellStock={sellStock}
        canBuyFuel={economy.savings >= currentPrice}
        canBuyStock={economy.savings >= currentPrice}
        canSellStock={economy.stock > 0}
      />
      <div style={{ margin: 12 }}>
        <span>Timer: {(timer / 25).toFixed(1)}s</span>
      </div>
      {gameState === "win" && (
        <div style={{ background: "#cfc", padding: 20, fontSize: 24 }}>
          Congratulations! You reached ${goalMoney.toFixed(2)}.
        </div>
      )}
      {gameState === "lose" && (
        <div style={{ background: "#fcc", padding: 20, fontSize: 24 }}>
          You did not reach ${goalMoney.toFixed(2)}.
          <button onClick={() => window.location.reload()} style={{ marginLeft: 20 }}>
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default GasMeUp;
